import type {
    ImMNElement,
    MNImageBackgroundRemovalContent,
    MNImageContent,
    MNImageEditorDataContent,
} from '../../../../../../common/elements/elementModelTypes';
import { MNImageBackgroundRemovalVariantOptions } from '../../../../../../common/elements/elementModelTypes';
import { asObject } from '../../../../../../common/utils/immutableHelper';
import {
    getImageBackgroundRemovalProp,
    getImageProp,
} from '../../../../../../common/elements/utils/elementPropertyUtils';

export const BackgroundRemovalVariantOptions = MNImageBackgroundRemovalVariantOptions;
type BackgroundRemovalVariantOptions = MNImageBackgroundRemovalVariantOptions;

const getInactiveVariant = (variant: BackgroundRemovalVariantOptions): BackgroundRemovalVariantOptions =>
    variant === BackgroundRemovalVariantOptions.withoutBackground
        ? BackgroundRemovalVariantOptions.withBackground
        : BackgroundRemovalVariantOptions.withoutBackground;

const getOriginalImageOrFallback = (image: MNImageContent | undefined): string | undefined =>
    image?.original || image?.large || image?.regular || image?.thumb;

const getBackgroundRemovalSourceFromContent = (
    backgroundRemoval: MNImageBackgroundRemovalContent,
    variant: BackgroundRemovalVariantOptions,
): MNImageContent | undefined => backgroundRemoval.sources?.[variant];

const getRenderedBackgroundRemovalImage = (
    backgroundRemoval: MNImageBackgroundRemovalContent,
    variant: BackgroundRemovalVariantOptions,
): MNImageContent | undefined =>
    backgroundRemoval.rendered?.[variant] ?? getBackgroundRemovalSourceFromContent(backgroundRemoval, variant);

const getActiveVariantFromBackgroundRemoval = (
    backgroundRemoval: MNImageBackgroundRemovalContent,
    currentImage: MNImageContent,
): BackgroundRemovalVariantOptions => {
    if (
        backgroundRemoval.active === BackgroundRemovalVariantOptions.withBackground ||
        backgroundRemoval.active === BackgroundRemovalVariantOptions.withoutBackground
    ) {
        return backgroundRemoval.active;
    }

    return getOriginalImageOrFallback(currentImage) ===
        getOriginalImageOrFallback(
            getRenderedBackgroundRemovalImage(backgroundRemoval, BackgroundRemovalVariantOptions.withoutBackground),
        )
        ? BackgroundRemovalVariantOptions.withoutBackground
        : BackgroundRemovalVariantOptions.withBackground;
};

const getBackgroundRemovalSources = (backgroundRemoval: MNImageBackgroundRemovalContent) => ({
    withBackground: getBackgroundRemovalSourceFromContent(
        backgroundRemoval,
        BackgroundRemovalVariantOptions.withBackground,
    ),
    withoutBackground: getBackgroundRemovalSourceFromContent(
        backgroundRemoval,
        BackgroundRemovalVariantOptions.withoutBackground,
    ),
});

/**
 * Read background-removal metadata as a plain object.
 */
export const getBackgroundRemoval = (element: ImMNElement): MNImageBackgroundRemovalContent =>
    asObject<MNImageBackgroundRemovalContent>(getImageBackgroundRemovalProp(element)) || {};

/**
 * Read the current image metadata as a plain object.
 */
const getCurrentImage = (element: ImMNElement): MNImageContent => asObject<MNImageContent>(getImageProp(element));

/**
 * Get the background-removal variant that is currently active on the element.
 */
export const getActiveBackgroundRemovalVariant = (element: ImMNElement): BackgroundRemovalVariantOptions =>
    getActiveVariantFromBackgroundRemoval(getBackgroundRemoval(element), getCurrentImage(element));

/**
 * Check if both full-source variants are available.
 */
export const hasBackgroundRemovalCache = (element: ImMNElement): boolean => {
    const backgroundRemoval = getBackgroundRemoval(element);
    return (
        !!getBackgroundRemovalSourceFromContent(backgroundRemoval, BackgroundRemovalVariantOptions.withBackground) &&
        !!getBackgroundRemovalSourceFromContent(backgroundRemoval, BackgroundRemovalVariantOptions.withoutBackground)
    );
};

/**
 * Check if the removed-background variant is currently active.
 */
export const isBackgroundRemovalActive = (element: ImMNElement): boolean =>
    getActiveBackgroundRemovalVariant(element) === BackgroundRemovalVariantOptions.withoutBackground;

/**
 * Get the full-size source for a background-removal variant.
 */
export const getBackgroundRemovalSource = (
    element: ImMNElement,
    variant: BackgroundRemovalVariantOptions,
): MNImageContent | undefined => getBackgroundRemovalSourceFromContent(getBackgroundRemoval(element), variant);

export const getBackgroundRemovalDisplayImage = (
    element: ImMNElement,
    variant: BackgroundRemovalVariantOptions,
): MNImageContent | undefined => getRenderedBackgroundRemovalImage(getBackgroundRemoval(element), variant);

/**
 * Get the full-source image the editor should load for the active variant.
 */
export const getBackgroundRemovalEditSource = (element: ImMNElement): MNImageContent | undefined =>
    getBackgroundRemovalSource(element, getActiveBackgroundRemovalVariant(element));

/**
 * Get the full-source image for the inactive variant so crop/rotate can be mirrored.
 */
export const getBackgroundRemovalInactiveSource = (element: ImMNElement): MNImageContent | undefined =>
    getBackgroundRemovalSource(element, getInactiveVariant(getActiveBackgroundRemovalVariant(element)));

type BuildBackgroundRemovalCropChangesOptions = {
    element: ImMNElement;
    imageOriginal: MNImageContent;
    imageEditorData: MNImageEditorDataContent;
    clearDrawing: boolean;
    preserveBackgroundRemoval: boolean;
};

/**
 * Persist crop metadata without changing the canonical full-source variants.
 */
export const buildBackgroundRemovalCropChanges = ({
    element,
    imageOriginal,
    imageEditorData,
    clearDrawing,
    preserveBackgroundRemoval,
}: BuildBackgroundRemovalCropChangesOptions) => {
    const backgroundRemoval = getBackgroundRemoval(element);

    return {
        imageOriginal,
        imageEditorData,
        backgroundRemoval: preserveBackgroundRemoval
            ? {
                  active: getActiveBackgroundRemovalVariant(element),
                  sources: getBackgroundRemovalSources(backgroundRemoval),
                  rendered: backgroundRemoval.rendered,
              }
            : null,
        file: null,
        media: null,
        drawing: clearDrawing ? null : undefined,
        hasDrawing: clearDrawing ? false : undefined,
    };
};

type BuildBackgroundRemovalRevertChangesOptions = {
    element: ImMNElement;
    imageOriginal: MNImageContent;
    clearDrawing: boolean;
    preserveBackgroundRemoval: boolean;
};

/**
 * Revert to the full-source variant and clear cached cropped outputs.
 */
export const buildBackgroundRemovalRevertChanges = ({
    element,
    imageOriginal,
    clearDrawing,
    preserveBackgroundRemoval,
}: BuildBackgroundRemovalRevertChangesOptions) => {
    const backgroundRemoval = getBackgroundRemoval(element);

    return {
        image: imageOriginal,
        imageOriginal: null,
        imageEditorData: null,
        backgroundRemoval: preserveBackgroundRemoval
            ? {
                  active: getActiveBackgroundRemovalVariant(element),
                  sources: getBackgroundRemovalSources(backgroundRemoval),
                  rendered: null,
              }
            : null,
        file: null,
        drawing: clearDrawing ? null : undefined,
        hasDrawing: clearDrawing ? false : undefined,
    };
};

type BuildBackgroundRemovalRenderedChangesOptions = {
    element: ImMNElement;
    uploadedImage: MNImageContent;
    variant: BackgroundRemovalVariantOptions;
    additionalRenderedImage?: MNImageContent;
    additionalRenderedVariant?: BackgroundRemovalVariantOptions;
};

/**
 * Store a cropped rendered variant without overwriting the full-source variants.
 */
export const buildBackgroundRemovalRenderedChanges = ({
    element,
    uploadedImage,
    variant,
    additionalRenderedImage,
    additionalRenderedVariant,
}: BuildBackgroundRemovalRenderedChangesOptions): MNImageBackgroundRemovalContent => {
    const backgroundRemoval = getBackgroundRemoval(element);
    const rendered = {
        ...(backgroundRemoval.rendered || {}),
        [variant]: uploadedImage,
        ...(additionalRenderedImage && additionalRenderedVariant
            ? { [additionalRenderedVariant]: additionalRenderedImage }
            : {}),
    };

    return {
        active: getActiveBackgroundRemovalVariant(element),
        sources: getBackgroundRemovalSources(backgroundRemoval),
        rendered,
    };
};

/**
 * Build the initial background-removal state after the provider returns the transparent source.
 */
export const buildInitialBackgroundRemoval = ({
    withBackgroundSource,
    withoutBackgroundSource,
}: {
    withBackgroundSource: MNImageContent;
    withoutBackgroundSource: MNImageContent;
}): MNImageBackgroundRemovalContent => ({
    active: BackgroundRemovalVariantOptions.withoutBackground,
    sources: {
        withBackground: withBackgroundSource,
        withoutBackground: withoutBackgroundSource,
    },
});

/**
 * Build top-level element changes when the user toggles between background variants.
 */
export const buildBackgroundRemovalToggleChanges = ({
    element,
    active,
}: {
    element: ImMNElement;
    active: boolean;
}): {
    image: MNImageContent;
    imageOriginal: MNImageContent | null;
    backgroundRemoval: MNImageBackgroundRemovalContent;
} | null => {
    const variant = active
        ? BackgroundRemovalVariantOptions.withoutBackground
        : BackgroundRemovalVariantOptions.withBackground;
    const image = getBackgroundRemovalDisplayImage(element, variant);
    const imageOriginal = getBackgroundRemovalSource(element, variant);

    if (!image || !imageOriginal) return null;

    return {
        image,
        imageOriginal:
            getOriginalImageOrFallback(image) === getOriginalImageOrFallback(imageOriginal) ? null : imageOriginal,
        backgroundRemoval: {
            ...getBackgroundRemoval(element),
            active: variant,
        },
    };
};

/**
 * Get the full-source image to use when resetting crop/rotation.
 */
export const getRevertedImage = (element: ImMNElement): MNImageContent | null =>
    getBackgroundRemovalSource(element, getActiveBackgroundRemovalVariant(element)) || null;
